// src/components/charts/CraftedCocktailsExpandCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Chart from "react-apexcharts";
import { fetchCraftedCocktailsExpand } from "../../actions/dashboard_actions";
import "react-datepicker/dist/react-datepicker.css";

/**
 * Reusable Crafted Cocktails chart with "apply-on-click" filters.
 * - Keeps chart mounted and hides it while applying/loading (no wiggle).
 * - Clicking Apply with identical criteria does nothing (no spinner).
 */
export default function CraftedCocktailsExpandCard({
  title = "CRAFTED COCKTAILS",
  className = "",
}) {
  const dispatch = useDispatch();

  // theme color from CSS
  const themeColor =
    getComputedStyle(document.body).getPropertyValue("--bs-theme").trim() ||
    "#08b0af";

  // options
  const periodOptions = [
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
    { value: "year", label: "Yearly" },
    // { value: "custom", label: "Custom" },
  ];

  // committed (drive fetching)
  const [period, setPeriod] = useState(periodOptions[1]); // weekly
  const [customFrom, setCustomFrom] = useState(new Date());
  const [customTo, setCustomTo] = useState(new Date());
  const [limit, setLimit] = useState(20);

  // local mirrors (user edits here)
  const [localPeriod, setLocalPeriod] = useState(period);
  const [localFrom, setLocalFrom] = useState(customFrom);
  const [localTo, setLocalTo] = useState(customTo);
  const [localLimit, setLocalLimit] = useState(limit);

  // sync locals if committed change externally
  useEffect(() => setLocalPeriod(period), [period]);
  useEffect(() => setLocalFrom(customFrom), [customFrom]);
  useEffect(() => setLocalTo(customTo), [customTo]);
  useEffect(() => setLocalLimit(limit), [limit]);

  // redux data
  const {
    crafted_cocktails_expand_info: data,
    loading_crafted_cocktails_expand: loading,
  } = useSelector((s) => s.dashboard);

  // applying control + post-paint commit
  const [isApplying, setIsApplying] = useState(false);
  const pendingApplyRef = useRef(null);
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setIsApplying(false);
      pendingApplyRef.current = null;
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  // equality helpers
  const eqOpt = (a, b) => (a?.value ?? null) === (b?.value ?? null);
  const eqDate = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return new Date(a).getTime() === new Date(b).getTime();
  };

  const unchanged = useMemo(() => {
    const sameP = eqOpt(localPeriod, period);
    const sameL = Number(localLimit) === Number(limit);
    const sameD =
      localPeriod?.value !== "custom"
        ? true
        : eqDate(localFrom, customFrom) && eqDate(localTo, customTo);
    return sameP && sameL && sameD;
  }, [localPeriod, localLimit, localFrom, localTo, period, limit, customFrom, customTo]);

  const handleApply = () => {
    if (unchanged) return; // no-op on identical filters
    pendingApplyRef.current = {
      nextPeriod: localPeriod,
      nextFrom: localFrom,
      nextTo: localTo,
      nextLimit: Math.max(1, Number(localLimit) || 1),
    };
    setIsApplying(true);
  };

  // commit after spinner paints
  useEffect(() => {
    if (!isApplying || !pendingApplyRef.current) return;
    const { nextPeriod, nextFrom, nextTo, nextLimit } = pendingApplyRef.current;
    setPeriod(nextPeriod);
    setCustomFrom(nextFrom);
    setCustomTo(nextTo);
    setLimit(nextLimit);
  }, [isApplying]);

  // fetch on committed changes
  useEffect(() => {
    const p = period.value;
    if (p === "custom") {
      dispatch(
        fetchCraftedCocktailsExpand(
          "custom",
          limit,
          customFrom.toISOString(),
          customTo.toISOString()
        )
      );
    } else {
      dispatch(fetchCraftedCocktailsExpand(p, limit));
    }
  }, [dispatch, period, limit, customFrom, customTo]);

  // build chart config
  const safeData = Array.isArray(data) ? data : [];
  const categories = safeData.map((r) => r.label);
  const values = safeData.map((r) => Number(r.value) || 0);
  const totalEver = safeData.reduce((s, r) => s + (Number(r.value) || 0), 0);

  const hideChart = isApplying || loading;

  const options = useMemo(
    () => ({
      chart: {
        id: 'crafted-cocktails',
        type: "line",
        toolbar: { show: false },
        animations: { enabled: !hideChart },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
      },
      xaxis: { categories, labels: { rotate: -45 } },
      stroke: { curve: "smooth", width: 2 },
      colors: [themeColor],
    }),
    [categories, themeColor, hideChart]
  );

  const series = useMemo(
    () => [{ name: "Crafted Cocktails", data: values }],
    [values]
  );

  return (
    <div className={className}>
      <h4 className="mb-1">{title}</h4>
      <div style={{ marginBottom: 12 }}>Total ever: {totalEver}</div>

      {/* centered filters + separate Apply */}
      <div className="d-flex justify-content-center align-items-end flex-wrap gap-3 mb-3">
        <div style={{ minWidth: 220 }}>
          <label>Date Dimension</label>
          <Select
            options={periodOptions}
            value={localPeriod}
            onChange={setLocalPeriod}
            classNamePrefix="react-select"
          />
        </div>

        {localPeriod?.value === "custom" && (
          <>
            <div style={{ minWidth: 220 }}>
              <label>From</label>
              <DatePicker
                selected={localFrom}
                onChange={setLocalFrom}
                className="form-control"
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <label>To</label>
              <DatePicker
                selected={localTo}
                onChange={setLocalTo}
                className="form-control"
              />
            </div>
          </>
        )}

        <div style={{ minWidth: 140 }}>
          <label>Limit</label>
          <input
            type="number"
            className="form-control"
            value={localLimit}
            min={1}
            onChange={(e) => setLocalLimit(e.target.value)}
          />
        </div>

        <div className="d-flex align-items-end">
          <button className="btn btn-secondary" onClick={handleApply}>
            <i className="fas fa-search" />
          </button>
        </div>
      </div>

      {/* Chart (kept mounted; hidden during apply/load) */}
      <div className="position-relative" style={{ minHeight: 320 }}>
        <div style={{ visibility: hideChart ? "hidden" : "visible" }}>
          <Chart options={options} series={series} type="line" height={300} />
        </div>

        {hideChart && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
            <div className="spinner-border" style={{ color: themeColor }} />
          </div>
        )}
      </div>
    </div>
  );
}
